import { useEffect, useMemo, useState } from 'react';
import type {
  BuildUp as BuildUpRow,
  Bucket,
  Command,
  FilterCondition,
  Perspective,
} from '@integrate/contracts';
import {
  FTE,
  HEADCOUNT,
  computeOneOffCosts,
  computeStageBridge,
  countsInHeadcount,
  currentBucket,
  bucketMetaFor,
  fmt,
  fmtMoney,
  getEmpField,
  isPlacedInTarget,
  passesFilters,
  tsaReplacementSide,
  type GroupingLevel,
  type Person,
  type SeparationState,
} from '@integrate/core';
import { TargetProgress } from './TargetProgress.js';
import { Modal } from '../ui/Modal.js';
import { StatTile } from '../ui/StatTile.js';
import { ICON, IconButton } from '../ui/Icon.js';
import { Section } from './sections.js';

/**
 * The Transfers & Day 1 phase panel, ported from `p3PanelHtml` / `p3ListHtml` / `wireP3Panel`
 * (orgchart.html:6139–6340).
 *
 * The perimeter took everyone dedicated enough to the carve-out to go with it. This phase takes the
 * people who are *shared* — the ones running payroll for both sides — and decides which of them the new
 * organisation needs on Day 1 anyway. That is a judgement rather than a rule, which is why the control is
 * a picker rather than a slider.
 *
 * The picker's state — the search text, the band, the grouping level, the ticked ids — is entirely
 * per-user and transient. None of it is a command, none of it is shared, and it is held here rather than
 * in the store because it means nothing outside this panel.
 */
export function TransfersPanel({
  state,
  perspective,
  filters,
  readOnly,
  scenarioId,
  onDispatch,
  onSelect,
  onNext,
  onOpenAssumptions,
  onOpenFullWidth,
}: {
  state: SeparationState;
  /**
   * This tab's perspective, passed rather than assumed.
   *
   * The picker honours the chart's active filters, and resolving a filter field goes through the
   * perspective's overrides — so the answer depends on which side you are looking at. The phase does
   * imply carve-out, but reaching for that implicitly is the ambient-lens mistake P2 spent its time
   * removing: the engine takes a state and a lens, and the caller holds them separately.
   */
  perspective: Perspective;
  /** This tab's active filters, passed like the perspective and for the same reason. */
  filters: readonly FilterCondition[];
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  /** Open somebody's panel — a newly created hire, so its timing can be set straight away. */
  onSelect(employeeId: string): void;
  onNext(): void;
  /**
   * Opens this panel full width, when there is somewhere for it to go.
   *
   * Passed by the side panel and NOT by the workbench, which is how one component serves both: the
   * button appears where the 320px column is the constraint, and does not appear once the constraint
   * is gone. Absent rather than disabled, because a control that offers to widen a surface already
   * as wide as the window is not offering anything.
   */
  onOpenFullWidth?(): void;
  onOpenAssumptions(): void;
}): JSX.Element | null {
  const [search, setSearch] = useState('');
  const [band, setBand] = useState<BandKey>('all');
  /** `null` = the default level; `''` = the flat, dedication-ranked list. */
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set());

  const hc = useMemo(() => computeStageBridge(state, { fn: HEADCOUNT }), [state]);
  const fte = useMemo(() => computeStageBridge(state, { fn: FTE }), [state]);
  /*
    RAW salary, not loaded cost — the panel's own caption says "Cost = raw annual salaries", and the
    legacy passes `e => Number(e.salary) || 0` (`:6229`). core's `costMetric(assumptions)` returns the
    LOADED figure, which would inflate this tile by the overhead multiplier and contradict the sentence
    underneath it.
  */
  const cost = useMemo(
    () => computeStageBridge(state, { fn: (p) => Number(p.salary) || 0 }),
    [state],
  );

  const transfers = useMemo(
    () =>
      state.employees.filter(
        (e) =>
          !e.isLeaver &&
          currentBucket(state.design, e.id) === 'Carve-out' &&
          bucketMetaFor(state.design, e.id)?.joinStage === 'day1',
      ),
    [state],
  );

  const level = groupLevel(state, groupKey);
  const candidates = useMemo(
    () => findCandidates(state, { search, band, perspective, filters }),
    [state, search, band, perspective, filters],
  );
  // The picker follows the chart's active filters verbatim, so it never offers somebody the chart is
  // hiding. Counting what that costs is the only way the hint below can be honest.
  const unfiltered = useMemo(
    () => findCandidates(state, { search, band, perspective, filters, ignoreFilters: true }),
    [state, search, band, perspective, filters],
  );
  const hiddenByFilters = filters.length ? unfiltered.length - candidates.length : 0;
  const shown = candidates.slice(0, LIST_CAP);

  const toggle = (id: string, on: boolean): void =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const toggleMany = (ids: readonly string[], on: boolean): void =>
    setChecked((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  /*
    Adding transfers is two writes per person — the bucket and the stage — and it must be one change.

    Sent as a batch of two bulk commands rather than one command per person: the server runs a batch in
    one transaction under one batch id, with each command seeing the previous one's writes, so
    `setJoinStage` finds everyone already in the carve-out. Forty transfers is two entries in the history
    and one undo step, which is what the shipped app's log batch achieves (`:6314`).
  */
  const addTransfers = async (): Promise<void> => {
    const ids = [...checked];
    if (!ids.length) return;
    await onDispatch([
      {
        type: 'setBucketBulk',
        scenarioId,
        assignments: ids.map((employeeId) => ({ employeeId, bucket: 'Carve-out' as Bucket })),
        // Explicitly manual: these people are being moved by hand, and the slider must not take them
        // back. Omitting it would carry a rule pick's source forward — right for a group reassignment,
        // wrong here, where the bulk IS the decision.
        src: 'manual',
      },
      {
        type: 'setJoinStage',
        scenarioId,
        assignments: ids.map((employeeId) => ({ employeeId, joinStage: 'day1' as const })),
      },
    ]);
    // Nothing to clear here: the effect below prunes the selection when the refetched design arrives.
  };

  /*
    The selection is pruned by what happened, not by the fact that something was attempted.

    `dispatch` resolves the same way whether the batch committed or was rejected — it surfaces a failure
    through the error banner and returns normally — so clearing after the await would throw away the
    user's whole selection on a failure they can see but can no longer act on. Dropping only the people
    who have stopped being candidates leaves a successful add with an empty picker and a rejected one
    with everything still ticked.

    It also handles the case nobody would think to write code for: a colleague moving someone this tab
    had ticked. Their row leaves the list, so the tick has to go with it.
  */
  useEffect(() => {
    setChecked((prev) => {
      if (!prev.size) return prev;
      const still = new Set<string>();
      for (const id of prev) {
        if (currentBucket(state.design, id) === 'Remain') still.add(id);
      }
      return still.size === prev.size ? prev : still;
    });
  }, [state]);

  if (!state.employees.length) return null;

  return (
    <>
      <Section
        testId="day1-strip"
        title="Day 1 organisation"
      >
        <div className="pp-stats">
          <StatTile label="Perimeter" value={fmt(hc.perimeter)} sub={`${fmt(fte.perimeter, 1)} FTE`} />
          <StatTile label="+ Transfers" value={fmt(hc.transfers)} sub={`${fmt(fte.transfers, 1)} FTE`} testId="day1-transfers" />
          <StatTile label="+ Build-ups (D1)" value={fmt(hc.d1BuildUps)} sub={`${fmt(fte.d1BuildUps, 1)} FTE`} />
          <StatTile
            label="= Day 1"
            value={fmt(hc.day1)}
            sub={`${fmt(fte.day1, 1)} FTE · ${fmtMoney(cost.day1)}`}
            testId="day1-total"
          />
        </div>
        <p className="muted">
          Day 1 typically runs below stand-alone size — some services continue under TSAs until replaced
          (phase 4). Cost = raw annual salaries.
        </p>
      </Section>

      <Section
        testId="transfer-picker"
        title={<>Transfer picker {checked.size > 0 && <span className="pill">{checked.size}</span>}</>}
        /*
          The heading's own action stays a SIBLING of the toggle rather than moving inside it. Nested
          interactive content is invalid HTML, the inner button stops being reachable by keyboard, and
          a press on "Full width →" would fold the section on its way to opening the workbench. As a
          sibling it is still `.side-panel h2 button` and the spacer the primitive supplies is still
          `.side-panel h2 .spacer`, so the heading lays out exactly as it did.

          The list this heading names is capped at 230px and truncates a name, a title and a unit into
          whatever is left of 320px. Both of those are the column, not the work — so there is a way out.
        */
        actions={
          onOpenFullWidth && (
            <button
              data-testid="transfers-full-width"
              title="Work through the candidates with the whole window"
              onClick={onOpenFullWidth}
            >
              Full width →
            </button>
          )
        }
      >
        <p className="muted">
          Shared employees to add into the carve-out at Day 1 — grouped by org unit, sorted by dedication
          within each group.
        </p>
        <input
          type="text"
          data-testid="picker-search"
          value={search}
          placeholder="Search name / title / unit…"
          style={{ width: '100%', marginBottom: 6 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="picker-controls">
          <span className="muted">Group by</span>
          <select
            data-testid="picker-group"
            value={level ? level.key : ''}
            onChange={(e) => setGroupKey(e.target.value)}
          >
            {state.groupingLevels.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
            <option value="">None — flat, by dedication</option>
          </select>
        </div>
        <div className="picker-bands">
          {BANDS.map((b) => (
            <button
              key={b.key}
              className={`toggle-btn ${band === b.key ? 'active' : ''}`}
              data-testid={`picker-band-${b.key}`}
              onClick={() => setBand(b.key)}
            >
              {b.label}
            </button>
          ))}
        </div>

        <div className="picker-list" data-testid="picker-list">
          {shown.length === 0 && <p className="muted">No candidates match.</p>}
          {level ? (
            groupCandidates(state, shown, level).map((g) => {
              const ids = g.members.map((m) => m.id);
              return (
                <div key={g.key}>
                  <label className="picker-group-head">
                    <input
                      type="checkbox"
                      data-testid={`picker-unit-${g.label}`}
                      checked={ids.every((id) => checked.has(id))}
                      disabled={readOnly}
                      title="Select the whole unit"
                      onChange={(e) => toggleMany(ids, e.target.checked)}
                    />
                    <span className="grow">
                      {g.ancestry && <span className="muted">{g.ancestry} › </span>}
                      <strong>{g.label}</strong>
                    </span>
                    <span className="muted">{fmt(g.members.length)}</span>
                  </label>
                  {g.members.map((p) => (
                    <CandidateRow
                      key={p.id}
                      person={p}
                      state={state}
                      checked={checked.has(p.id)}
                      disabled={readOnly}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              );
            })
          ) : (
            shown.map((p) => (
              <CandidateRow
                key={p.id}
                person={p}
                state={state}
                checked={checked.has(p.id)}
                disabled={readOnly}
                onToggle={toggle}
              />
            ))
          )}
        </div>

        {/* No silent truncation: a capped list that did not say so would read as "these are all of them". */}
        {candidates.length > LIST_CAP && (
          <p className="muted" data-testid="picker-capped">
            Showing {LIST_CAP} of {fmt(candidates.length)} — refine the search.
          </p>
        )}
        {hiddenByFilters > 0 && (
          <p
            className="muted"
            data-testid="picker-filtered"
            title="The picker follows the chart's filters — clear them to see everyone"
          >
            {fmt(hiddenByFilters)} candidate{hiddenByFilters === 1 ? '' : 's'} hidden by chart filters.
          </p>
        )}

        <button
          className="primary"
          style={{ width: '100%', marginTop: 8 }}
          data-testid="picker-add"
          disabled={readOnly || checked.size === 0}
          onClick={() => void addTransfers()}
        >
          Add {checked.size > 0 ? `${checked.size} ` : ''}as Day 1 transfer{checked.size === 1 ? '' : 's'}
        </button>
      </Section>

      <Section
        testId="current-transfers"
        title={
          <>
            Current transfers <span className="pill">{fmt(transfers.length)}</span>
          </>
        }
      >
        {/*
          The list and the tile above it count different populations, on purpose. The tile is a bridge
          figure and excludes open positions and inactive people; the list is where a transfer is
          removed, so hiding one would make it unreachable. Naming the gap is better than either.
        */}
        {transfers.length !== hc.transfers && (
          <p className="muted" data-testid="transfer-count-gap">
            {fmt(Math.abs(transfers.length - hc.transfers))} of these are open positions or inactive
            people — listed here so they can be removed, but not counted in the tile above.
          </p>
        )}
        {transfers.length === 0 ? (
          <p className="muted">None yet.</p>
        ) : (
          <>
            {transfers.slice(0, TRANSFER_CAP).map((e) => (
              <div className="sp-row" key={e.id}>
                <span className="k grow">{e.name}</span>
                <span className="v">
                  {e.dedication != null ? `${Math.round(e.dedication * 100)}%` : '—'}
                  <IconButton
                    icon={ICON.remove}
                    testId={`transfer-remove-${e.id}`}
                    label="Remove the transfer (back to Remain / rule-eligible)"
                    disabled={readOnly}
                    /*
                      Back to UNDECIDED, not to an explicit Remain (`:6327`). The distinction is the whole
                      perimeter model: an absent decision reads as Remain and stays eligible for the
                      threshold rule, while an explicit Remain is a human decision the slider never
                      revisits. Removing a transfer means "I did not mean to move them", not "they stay
                      whatever the rule says".
                    */
                    onClick={() =>
                      void onDispatch({
                        type: 'setBucket',
                        scenarioId,
                        employeeId: e.id,
                        bucket: null,
                      })
                    }
                  />
                </span>
              </div>
            ))}
            {transfers.length > TRANSFER_CAP && (
              <p className="muted">+ {fmt(transfers.length - TRANSFER_CAP)} more…</p>
            )}
          </>
        )}
      </Section>

      <BuildUps
        state={state}
        readOnly={readOnly}
        scenarioId={scenarioId}
        onDispatch={onDispatch}
        onSelect={onSelect}
        onOpenAssumptions={onOpenAssumptions}
      />

      {state.targetEnabled && <TargetProgress state={state} />}

      <button className="primary" style={{ width: '100%' }} data-testid="transfers-next" onClick={onNext}>
        Next: TSA &amp; Day 2 →
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * The picker's vocabulary
 * ------------------------------------------------------------------ */

const LIST_CAP = 120;
const TRANSFER_CAP = 60;

type BandKey = 'all' | 'hi' | 'mid' | 'lo' | 'none';

/** Ported verbatim from `P3_BANDS` (`:6167`). The bands partition the dedicated population. */
const BANDS: readonly { key: BandKey; label: string; test(d: number | null): boolean }[] = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'hi', label: '≥ 50%', test: (d) => d != null && d >= 0.5 },
  { key: 'mid', label: '25–50%', test: (d) => d != null && d >= 0.25 && d < 0.5 },
  { key: 'lo', label: '< 25%', test: (d) => d != null && d < 0.25 },
  { key: 'none', label: 'No data', test: (d) => d == null },
];

/**
 * The grouping level, resolved defensively (`p3GroupLevel`, `:6159`).
 *
 * A stored key must still exist — an import can swap the whole hierarchy underneath it — and the default
 * is the SECOND level, the same "e.g. Function" default the breakdown tables use, because grouping a
 * picker by Division puts everyone in three enormous buckets.
 */
function groupLevel(state: SeparationState, groupKey: string | null): GroupingLevel | null {
  const levels = state.groupingLevels;
  if (groupKey === '' || !levels.length) return null;
  return levels.find((l) => l.key === groupKey) ?? levels[Math.min(1, levels.length - 1)] ?? null;
}

/**
 * Who can be transferred, ported from `p3Candidates` (`:6172`).
 *
 * Real, active people currently outside the carve-out. Leavers and open positions are not people you can
 * move; someone already in the carve-out is already in it; and the chart's filters apply because the
 * picker must not offer somebody the chart is hiding.
 */
function findCandidates(
  state: SeparationState,
  options: {
    search: string;
    band: BandKey;
    perspective: Perspective;
    filters: readonly FilterCondition[];
    ignoreFilters?: boolean;
  },
): Person[] {
  const q = options.search.trim().toLowerCase();
  const band = BANDS.find((b) => b.key === options.band) ?? BANDS[0]!;
  const filterCtx = { targetOverrides: state.targetOverrides, design: state.design };

  return state.employees
    .filter(
      (e) =>
        !e.isLeaver &&
        countsInHeadcount(e) &&
        currentBucket(state.design, e.id) === 'Remain',
    )
    .filter((e) => options.ignoreFilters || passesFilters(filterCtx, e, options.perspective, options.filters))
    .filter((e) => band.test(e.dedication))
    .filter(
      (e) =>
        !q ||
        e.name.toLowerCase().includes(q) ||
        (e.jobTitle ?? '').toLowerCase().includes(q) ||
        // Base values, as the original searches (`e[l.key]`, `:6182`): a scenario rename should not
        // change what a person can be found by while you are choosing who to move.
        state.groupingLevels.some((l) =>
          String(getEmpField(e, l.key, 'as-is', undefined) ?? '').toLowerCase().includes(q),
        ),
    )
    .sort(
      (a, b) =>
        (b.dedication ?? -1) - (a.dedication ?? -1) || a.name.localeCompare(b.name),
    );
}

interface CandidateGroup {
  key: string;
  ancestry: string;
  label: string;
  blank: boolean;
  members: Person[];
}

/**
 * Group the shown candidates by a level, ancestry-qualified (`:6193`).
 *
 * Two teams called "Operations" under different divisions are different units, so the key carries the
 * path above them and the header shows it. A blank at the chosen level folds into "(No <label>)" and
 * sorts last, because it is an absence rather than a unit.
 */
function groupCandidates(
  state: SeparationState,
  shown: readonly Person[],
  level: GroupingLevel,
): CandidateGroup[] {
  const idx = state.groupingLevels.findIndex((l) => l.key === level.key);
  const groups = new Map<string, CandidateGroup>();

  for (const person of shown) {
    const value = String(getEmpField(person, level.key, 'as-is', undefined) ?? '').trim();
    const ancestry = state.groupingLevels
      .slice(0, idx)
      .map((l) => String(getEmpField(person, l.key, 'as-is', undefined) ?? '').trim())
      .filter(Boolean)
      .join(' › ');
    const blank = !value;
    const label = blank ? `(No ${level.label})` : value;
    const key = blank ? label : `${ancestry} › ${label}`;

    const existing = groups.get(key);
    if (existing) existing.members.push(person);
    else groups.set(key, { key, ancestry: blank ? '' : ancestry, label, blank, members: [person] });
  }

  return [...groups.values()].sort(
    (a, b) => Number(a.blank) - Number(b.blank) || a.key.localeCompare(b.key),
  );
}

/* ------------------------------------------------------------------ */

function CandidateRow({
  person,
  state,
  checked,
  disabled,
  onToggle,
}: {
  person: Person;
  state: SeparationState;
  checked: boolean;
  disabled: boolean;
  onToggle(id: string, on: boolean): void;
}): JSX.Element {
  const lvl1 = state.groupingLevels[0];
  const sub =
    person.jobTitle ||
    (lvl1 ? String(getEmpField(person, lvl1.key, 'as-is', undefined) ?? '') : '') ||
    '—';
  return (
    <label className="picker-row">
      <input
        type="checkbox"
        data-testid={`picker-pick-${person.id}`}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle(person.id, e.target.checked)}
      />
      <span className="grow">
        {person.name} <span className="muted">· {sub}</span>
      </span>
      <span className="muted num">
        {person.dedication != null ? `${Math.round(person.dedication * 100)}%` : '—'}
      </span>
    </label>
  );
}

/**
 * The build-up section, ported from the `ph3-bu` block (`:6266`) and the modal at `:1386`.
 *
 * A build-up is a position the new organisation needs and the old one does not have anybody in — a
 * capability the carve-out was buying from its parent, a role the seller kept centrally, a
 * dis-synergy. It is not a transfer, so it does not come out of the picker above: nobody is being
 * moved, somebody is being hired.
 *
 * The list is here rather than in a register modal because a build-up is a PERSON: it lands on the
 * chart, carries a bucket, can head a group, and its timing is edited on its own panel like anyone
 * else's. So this offers the two things a list has to — add, and go to — and everything else happens
 * where it happens for real people.
 */
function BuildUps({
  state,
  readOnly,
  scenarioId,
  onDispatch,
  onSelect,
  onOpenAssumptions,
}: {
  state: SeparationState;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  onSelect(employeeId: string): void;
  onOpenAssumptions(): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const oneOff = computeOneOffCosts(state);
  const positions = oneOff.carveBuN + oneOff.remainBuN;
  const buildUps = state.design.buildUps;

  return (
    <Section
      testId="build-ups"
      title={
        <>
          Build-ups {buildUps.length > 0 && <span className="pill">{fmt(buildUps.length)}</span>}
        </>
      }
    >
      <p className="muted">
        New positions the Day 1 organisation needs — capability the carve-out was buying from its
        parent, or a role that stayed behind. Only assignable to Remain or Carve-out.
      </p>
      {positions > 0 && (
        <div
          className="sp-row"
          title={`${state.assumptions.hiringCostPct}% of each build-up's annual salary`}
        >
          <span className="k">
            One-off hiring cost ({fmt(positions)} position{positions === 1 ? '' : 's'}) —{' '}
            <button type="button" className="linkish" data-testid="asm-open-transfers" onClick={onOpenAssumptions}>
              assumptions
            </button>
          </span>
          <span className="v" data-testid="buildup-hiring-total">
            {fmtMoney(oneOff.hiring)}
          </span>
        </div>
      )}

      {buildUps.length > 0 && (
        <div data-testid="buildup-list">
          {buildUps.map((b) => {
            const bucket = currentBucket(state.design, b.id);
            return (
              <div className="sp-row" key={b.id}>
                <span className="k">
                  <button
                    className="linkish"
                    data-testid={`buildup-open-${b.id}`}
                    onClick={() => onSelect(b.id)}
                  >
                    {b.name}
                  </button>
                </span>
                <span className="v">
                  {bucket === 'Carve-out'
                    ? `Carve-out · ${b.wave === 'day2' ? 'Day 2' : 'Day 1'}`
                    : bucket}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <button data-testid="buildup-add-open" onClick={() => setOpen(true)}>
          + Add build-up
        </button>
      )}

      {open && (
        <BuildUpForm
          state={state}
          scenarioId={scenarioId}
          onDispatch={onDispatch}
          onSelect={onSelect}
          onOpenAssumptions={onOpenAssumptions}
          onClose={() => setOpen(false)}
        />
      )}
    </Section>
  );
}

/**
 * The creation form, ported from `openBuildUpForm` / `syncBuWaveUI` / `saveBuildUp` (`:7108`–`:7245`).
 *
 * Everything here is a DRAFT until Add is pressed, unlike the app's column-mapping modal but like its
 * build-up one — the app builds the whole object in `saveBuildUp` and pushes it once. That matters more
 * on the platform than it did there: a field-by-field version would be one event per keystroke in a
 * shared change log.
 *
 * Three couplings are the app's, and each of them exists because a wrong answer here is expensive:
 *
 *   · A linked TSA is authoritative for the SIDE. Whoever loses the service when the agreement ends is
 *     the side that has to build the replacement, so linking a received TSA forces Carve-out and a
 *     provided one forces Remain. `saveBuildUp` re-derives this at save rather than trusting the
 *     toggle, and so does this — a stale toggle would otherwise put the replacement on the wrong side,
 *     where `tsaReplacements` would not count it and the register would report the service uncovered.
 *   · A carve-side linked hire is Day 2 by definition: the replacement exists so the TSA can end.
 *   · The wave only means anything for a carve-out hire, so the toggle is hidden for a Remain one and
 *     the value sent is Day 1.
 *
 * Creating a hire is TWO commands — the row, then its bucket — sent as one batch so undo takes back a
 * position and its assignment together rather than leaving an unbucketed hire behind. The app does the
 * same two writes in `saveBuildUp` under one log entry.
 */
export function BuildUpForm({
  state,
  scenarioId,
  onDispatch,
  onSelect,
  onOpenAssumptions,
  onClose,
  initialTsaId = '',
}: {
  state: SeparationState;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  onSelect(employeeId: string): void;
  onOpenAssumptions(): void;
  onClose(): void;
  /**
   * A TSA this hire replaces, chosen before the form opened.
   *
   * The exit timeline's whole output is rows saying a replacement is missing, and until now there was
   * no way to create one from where that is shown — the register's footer offered "Add TSA" alone. The
   * link decides the side and the wave (see `bucket` below), so pre-selecting it is the entire fix.
   */
  initialTsaId?: string;
}): JSX.Element {
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [salary, setSalary] = useState('0');
  const [fte, setFte] = useState('1');
  const [location, setLocation] = useState('');
  const [seniority, setSeniority] = useState('');
  const [country, setCountry] = useState('');
  const [legalEntity, setLegalEntity] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [levels, setLevels] = useState<Record<string, string>>({});
  const [bucketChoice, setBucketChoice] = useState<Bucket>('Carve-out');
  /*
    Day 1, because that is what this panel is about.

    The app's default is phase-dependent — `_buWaveChoice = … state.phase === 'tsa' ? 'day2' : 'day1'`
    (`:7150`) — and its Day 1 panel prefills `{bucket: 'Carve-out', wave: 'day1'}` explicitly (`:6337`).
    Defaulting to Day 2 here meant somebody typed a name under "Day 1 organisation", pressed Add, and
    watched the Build-ups (D1) tile, the Day 1 total and the phase chip all stay exactly where they
    were — a Day 2 hire counts in `d2BuildUps`, and the Day 1 stage lens does not even draw it.
  */
  const [wave, setWave] = useState<'day1' | 'day2'>('day1');
  const [tsaId, setTsaId] = useState(initialTsaId);
  const [neededBy, setNeededBy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The id we are waiting to see land. Non-null between pressing Add and the design coming back. */
  const [pending, setPending] = useState<string | null>(null);

  const linked = tsaId ? state.design.tsas.find((t) => t.id === tsaId) ?? null : null;
  // The link decides the side and, on the carve side, the wave. Derived rather than stored, so the
  // controls cannot disagree with what will be sent.
  const bucket: Bucket = linked ? tsaReplacementSide(linked) : bucketChoice;
  const carve = bucket === 'Carve-out';
  const effectiveWave: 'day1' | 'day2' = !carve ? 'day1' : linked ? 'day2' : wave;
  const showTsa = state.design.tsas.length > 0 || (carve && effectiveWave === 'day2');
  const showNeededBy = Boolean(linked) || (carve && effectiveWave === 'day2');

  const suggestions = useMemo(() => {
    const of = (pick: (p: Person) => unknown): string[] => {
      const seen = new Set<string>();
      for (const e of state.employees) {
        const value = String(pick(e) ?? '').trim();
        if (value) seen.add(value);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    };
    return {
      country: of((p) => p.country),
      legalEntity: of((p) => p.legalEntity),
      levels: Object.fromEntries(
        state.groupingLevels.map((l) => [l.key, of((p) => p.levels?.[l.key])]),
      ) as Record<string, string[]>,
    };
  }, [state]);

  // Supervisors are real people, sorted by name — the original's list (`:7139`), and the reason a
  // planned hire cannot report to another planned hire.
  const supervisors = useMemo(
    () => [...state.employees].sort((a, b) => a.name.localeCompare(b.name)),
    [state.employees],
  );

  /*
    Close when the row actually lands, not when the request returns.

    `dispatch` resolves the same way whether the batch committed or was rejected — it puts a banner up
    and returns normally — so closing after the await threw away every field the user had typed on any
    refusal: an off-roster supervisor, a TSA a colleague had just deleted, a role that turned
    read-only, a dropped connection. It then selected the id, so the panel showed "The whole
    organisation" for a position that was never created while the banner said the change failed. This
    is the same rule the picker above follows for its ticks, and for the same reason.
  */
  useEffect(() => {
    if (pending && state.design.buildUps.some((b) => b.id === pending)) {
      onClose();
      // Straight to the new hire's panel: its wave, TSA link and needed-by are edited there, and
      // landing on it is what tells the user the position exists.
      onSelect(pending);
    }
  }, [state, pending, onClose, onSelect]);

  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    setBusy(true);
    setError(null);

    const id = `bu-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const nb = neededBy.trim();
    const buildUp: BuildUpRow = {
      id,
      name: trimmed,
      supervisorId: supervisorId || null,
      jobTitle: jobTitle.trim() || null,
      location: location.trim() || null,
      country: country.trim() || null,
      currency: null,
      pensionScheme: null,
      seniority: seniority.trim() || null,
      businessUnit: null,
      legalEntity: legalEntity.trim() || null,
      salary: Number(salary) || 0,
      /*
        FTE falls back to 1, not to 0. `Number($('buFte').value) || 1` in the original (`:7217`) — a
        position nobody has sized is a whole one, and a zero here would silently exclude the hire from
        every FTE figure while still counting in headcount.
      */
      fte: Number(fte) || 1,
      levels: Object.fromEntries(
        state.groupingLevels.map((l) => [l.key, (levels[l.key] ?? '').trim() || null]),
      ),
      wave: effectiveWave,
      tsaId: showTsa && tsaId ? tsaId : null,
      // The TSA clock, 0..120 — see the needed-by input on the person panel for why not 36.
      neededBy: showNeededBy && nb !== '' ? Math.max(0, Math.min(120, Math.round(Number(nb) || 0))) : null,
      day2Exit: false,
      startMonth: null,
    };

    setPending(id);
    await onDispatch([
      { type: 'addBuildUp', scenarioId, buildUp },
      { type: 'setBucket', scenarioId, employeeId: id, bucket },
    ]);
    setBusy(false);
  };

  return (
    <Modal
      testId="buildup-form"
      title="Add build-up personnel"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button onClick={onClose}>Cancel</button>
          <button className="primary" data-testid="bu-save" disabled={busy} onClick={() => void submit()}>
            Add to chart
          </button>
        </>
      }
    >
      <p className="muted">
        Build-up personnel are added on top of the existing org to cover capability gaps (e.g.
        carve-out dis-synergies). Only assignable to Remain or Carve-out.
      </p>

      <div className="field">
        <label htmlFor="bu-name">Name *</label>
        <input
          id="bu-name"
          data-testid="bu-name"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {state.groupingLevels.map((l) => (
        <div className="field" key={l.key}>
          <label htmlFor={`bu-${l.key}`}>{l.label}</label>
          <input
            id={`bu-${l.key}`}
            data-testid={`bu-level-${l.key}`}
            list={`bu-list-${l.key}`}
            value={levels[l.key] ?? ''}
            onChange={(e) => setLevels((prev) => ({ ...prev, [l.key]: e.target.value }))}
          />
          <datalist id={`bu-list-${l.key}`}>
            {(suggestions.levels[l.key] ?? []).map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
      ))}

      <div className="field">
        <label htmlFor="bu-title">Job title</label>
        <input id="bu-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
      </div>

      <div className="field-grid">
        <div className="field">
          <label htmlFor="bu-salary">Salary</label>
          <input
            id="bu-salary"
            data-testid="bu-salary"
            type="number"
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="bu-fte">FTE</label>
          <input
            id="bu-fte"
            type="number"
            step="0.05"
            value={fte}
            onChange={(e) => setFte(e.target.value)}
          />
        </div>
      </div>
      <p className="muted" data-testid="bu-hiring-note">
        One-off hiring cost:{' '}
        {fmtMoney(((Number(salary) || 0) * (Number(state.assumptions.hiringCostPct) || 0)) / 100)}{' '}
        ({state.assumptions.hiringCostPct}% of annual salary —{' '}
        <button type="button" className="linkish" data-testid="asm-open-buildup-form" onClick={onOpenAssumptions}>
          cost assumptions
        </button>
        ).
      </p>

      <div className="field-grid">
        <div className="field">
          <label htmlFor="bu-location">Location</label>
          <input id="bu-location" value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="bu-seniority">Seniority</label>
          <input
            id="bu-seniority"
            data-testid="bu-seniority"
            value={seniority}
            onChange={(e) => setSeniority(e.target.value)}
          />
        </div>
      </div>

      <div className="field-grid">
        <div className="field">
          <label htmlFor="bu-country">Country</label>
          <input
            id="bu-country"
            list="bu-country-list"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
          <datalist id="bu-country-list">
            {suggestions.country.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="bu-entity">Legal entity</label>
          <input
            id="bu-entity"
            list="bu-entity-list"
            value={legalEntity}
            onChange={(e) => setLegalEntity(e.target.value)}
          />
          <datalist id="bu-entity-list">
            {suggestions.legalEntity.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="field">
        <label htmlFor="bu-supervisor">Reports to (supervisor)</label>
        <select
          id="bu-supervisor"
          data-testid="bu-supervisor"
          value={supervisorId}
          onChange={(e) => setSupervisorId(e.target.value)}
        >
          <option value="">— Top level —</option>
          {supervisors.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.id})
            </option>
          ))}
        </select>
        {/* The app's own wording (`:1413`), true again now that the layout is reachable. */}
        <p className="muted">
          Used by the reporting-line view; optional for grouped views, which place the hire by its
          level values.
        </p>
      </div>

      <div className="field">
        <label>Bucket *</label>
        <div className="toggle-row">
          {(['Remain', 'Carve-out'] as const).map((b) => (
            <button
              key={b}
              className={`toggle-btn ${bucket === b ? 'active' : ''}`}
              data-testid={`bu-bucket-${b.replace(/\s+/g, '-')}`}
              aria-pressed={bucket === b}
              // Locked by a link: the side that loses the service is the side that must replace it.
              disabled={Boolean(linked)}
              onClick={() => setBucketChoice(b)}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {carve && (
        <div className="field">
          <label>Wave (carve-out stage)</label>
          <div className="toggle-row">
            <button
              className={`toggle-btn ${effectiveWave === 'day1' ? 'active' : ''}`}
              data-testid="bu-wave-day1"
              aria-pressed={effectiveWave === 'day1'}
              title="Position exists from legal separation (Day 1)"
              disabled={Boolean(linked)}
              onClick={() => setWave('day1')}
            >
              Day 1
            </button>
            <button
              className={`toggle-btn ${effectiveWave === 'day2' ? 'active' : ''}`}
              data-testid="bu-wave-day2"
              aria-pressed={effectiveWave === 'day2'}
              title="Post-separation build-up (e.g. replacing a TSA service)"
              disabled={Boolean(linked)}
              onClick={() => setWave('day2')}
            >
              Day 2
            </button>
          </div>
        </div>
      )}

      {showTsa && (
        <div className="field">
          <label htmlFor="bu-tsa">Replaces TSA (optional)</label>
          <select
            id="bu-tsa"
            data-testid="bu-tsa"
            value={tsaId}
            onChange={(e) => setTsaId(e.target.value)}
          >
            <option value="">— No TSA link —</option>
            {state.design.tsas.map((t) => (
              <option key={t.id} value={t.id}>
                {t.direction === 'provided' ? '⇠' : '⇢'} {t.name}
                {t.durationMonths ? ` (exit M${t.durationMonths})` : ''} →{' '}
                {tsaReplacementSide(t) === 'Remain' ? 'RemainCo builds' : 'carve-out builds'}
              </option>
            ))}
          </select>
          <p className="muted" data-testid="bu-tsa-hint">
            {!linked
              ? 'The TSA’s direction sets the side: ⇢ received → the carve-out builds the replacement (Day 2); ⇠ provided → RemainCo builds it.'
              : linked.direction === 'provided'
                ? `⇠ Provided (reverse) TSA — RemainCo internalises “${linked.name}” when it ends, so this hire sits in the Remain organisation.`
                : `⇢ Received TSA — the carve-out builds this replacement (Day 2) so “${linked.name}” can exit.`}
          </p>
        </div>
      )}

      {showNeededBy && (
        <div className="field">
          <label htmlFor="bu-needed-by">Needed by (months after Day 1)</label>
          <input
            id="bu-needed-by"
            data-testid="bu-needed-by"
            type="number"
            min={0}
            /* The TSA clock, not the plan's 36 — see the person panel's needed-by input. */
            max={120}
            step={1}
            placeholder="e.g. 9"
            value={neededBy}
            onChange={(e) => setNeededBy(e.target.value)}
          />
        </div>
      )}

      {error && (
        <p className="error" data-testid="bu-error">
          {error}
        </p>
      )}
    </Modal>
  );
}

